
  import { Component, Prop, Vue, Watch } from 'vue-property-decorator'
  import { mapGetters } from 'vuex'

  import type { TixTimeDuration } from '@/TixTime/Duration'
  import ApplyPromoCodes from '@/checkout/components/ApplyPromoCodes.vue'
  import type { WalletType } from '@/checkout/helpers/wallets'
  import StripeWalletPayment from '@/checkout/stripe/StripeWalletPayment'
  import type { PaymentMethodName } from '@/checkout/stripe/helpers'
  import ExpiryTimer from '@/components/cart/ExpiryTimer.vue'
  import Price from '@/components/elements/Price.vue'
  import { type PayButtonContext, onSubmit } from '@/helpers/CartHelpers'
  import { formatCurrency } from '@/helpers/Currency'
  import { formatCartItemDateTime } from '@/helpers/Date'
  import { theme } from '@/helpers/Environment'
  import type { LanguageStrings } from '@/language/types'
  import { cartExpiresIn } from '@/state/Cart'
  import type { PendingGiftCardPayment } from '@/store/Cart'
  import { CartItem, TimedItem, isTimedItem } from '@/store/CartItem'
  import store from '@/store/store'
  import type { ComponentOptions } from '@/types/ComponentOptions'

  import PayButton from './PayButton.vue'
  import RemoveCartItemOverlay from './RemoveCartItemOverlay.vue'

  type CartWidgetItem = CartItem & {
    isPendingRemoval?: boolean
  }

  @Component({
    name: 'CartWidget',
    components: {
      ExpiryTimer,
      ApplyPromoCodes,
      Price,
      RemoveCartItemOverlay,
      PayButton,
    },
    computed: mapGetters({
      memberIdentity: 'Member/identity',
      cart: 'Cart/cart',
      cartItems: 'Cart/cartItems',
      cartMods: 'Cart/cartMods',
      faceValue: 'Cart/faceValue',
      totalPromoDiscounts: 'Cart/totalPromoDiscounts',
      totalFixedFees: 'Cart/totalFixedFees',
      totalPercentFees: 'Cart/totalPercentFees',
      totalPayments: 'Cart/totalPayments',
      paymentDue: 'Cart/paymentDue',
      pendingGiftCardPayments: 'Cart/pendingGiftCardPayments',
      paymentDueByCreditCard: 'Cart/paymentDueByCreditCard',
    }),
    methods: {
      formatCurrency,
    },
  })
  export default class extends Vue {
    t: LanguageStrings['cartWidget']
    opt: ComponentOptions['cartWidget']

    @Prop({ default: false })
    submitDisabled: boolean

    @Prop({ default: 'default' })
    context: PayButtonContext

    @Prop()
    selectedPaymentType: PaymentMethodName

    @Prop()
    supportedWallet: WalletType

    @Prop({ default: false })
    submitting: boolean

    @Prop({ default: false })
    isEmbedded: boolean

    @Prop()
    wallet: StripeWalletPayment | null

    // Let TypeScript know about the getters.
    // Unfortunately vuex-class doesn't work with modules
    // @see https://github.com/ktsn/vuex-class
    pendingGiftCardPayments: PendingGiftCardPayment[]
    pendingRemovalCartItemId: string | null = null
    pendingRemovalTriggerElement: HTMLElement | null = null
    cart
    cartItems: CartItem[]
    cartMods: CartMod[]
    faceValue
    totalPayments
    paymentDue
    paymentDueByCreditCard
    totalFixedFees
    totalPercentFees
    totalPromoDiscounts

    expiresIn: TixTimeDuration | null = null
    expiryInterval: ReturnType<typeof setTimeout>

    // Bundle expansion state
    expandedBundles: Record<string, boolean> = {}

    distanceTopViewport = 0

    created() {
      this.setExpiry()

      this.expiryInterval = setInterval(this.setExpiry, 1000)
    }

    mounted() {
      this.setDistanceTopViewport()
      window.addEventListener('scroll', this.setDistanceTopViewport)
    }

    setDistanceTopViewport() {
      const cart = this.$refs.cart as HTMLElement

      if (!this.isEmbedded) {
        const rect = cart.getBoundingClientRect()
        // Minimum value of 20 as the .aside has `top: 20px`.
        this.distanceTopViewport = Math.max(rect.top, 20)
      }
    }

    @Watch('distanceTopViewport')
    setMaxHeight() {
      const cart = this.$refs.cart as HTMLElement
      // This is to make sure that cart doesn't go shorter than this value in
      // order to guarantee that an appropriate real estate is given to cart
      // items.
      const minHeight = this.opt.minHeight ?? '500px'
      // Give it a 20px margin on the bottom.
      // .aside has `position: sticky` and `top: 20px` ensuring there is a 20px margin at the top.
      cart.style.maxHeight = `max(${minHeight}, calc(100vh - ${this.distanceTopViewport + 20}px))`
    }

    destroyed() {
      clearInterval(this.expiryInterval)
      window.removeEventListener('scroll', this.setDistanceTopViewport)
    }

    get empty() {
      return this.cartItems.length < 1
    }

    get promoCodeApplyEnabled() {
      return this.isModifiable && this.paymentDue > 0 && this.totalPayments === 0 && !this.blocked
    }

    get showTotals(): boolean {
      return this.paymentDueByCreditCard !== 0 || this.faceValue !== 0
    }

    get discounts() {
      // Hide checkout rules that block checkout but don't discount the value.
      // Show checkout rules that don't block checkout or offer a discount.
      // They probably provide other benefits, such as extended two-year membership.
      return this.cartMods.filter((mod) => !(mod.blocking_checkout && mod.discount === '0'))
    }

    setExpiry() {
      this.expiresIn = cartExpiresIn()
    }

    get items(): CartWidgetItem[] {
      return this.cartItems.map((item) => {
        const isPendingRemoval = item.id === this.pendingRemovalCartItemId

        const updatedItem: CartWidgetItem = { ...item, isPendingRemoval }

        if (item.isMembershipEvent) {
          const types = item.types.filter((type) => type.totalPrice > 0)
          return {
            ...updatedItem,
            // Membership cart items show the ticket group name instead of the event name
            name: types.length > 0 ? types[0].group.name : item.name,
            types,
          }
        } else {
          return {
            ...updatedItem,
            types: item.types.map((type) => ({
              ...type,
              name: this.opt.showTicketGroupName ? `${type.group.name} - ${type.name}` : type.name,
            })),
          }
        }
      })
    }

    get hasUpsells() {
      return this.items.some((item) => !item.isAnchor)
    }

    itemDateTime(item: CartItem): string | null {
      if (!isTimedItem(item)) {
        return null
      }

      const dateTimeFormat = this.opt.dateTimeFormat ?? 'ddd, MMM D, YYYY [at] h:mm A'
      const dateOnlyFormat = this.opt.dateOnlyFormat ?? 'ddd, MMM D, YYYY'
      const timeOnlyFormat = this.opt.timeOnlyFormat ?? 'h:mm A'
      return formatCartItemDateTime(item as TimedItem, {
        dateTimeFormat,
        dateOnlyFormat,
        timeOnlyFormat,
      })
    }

    // Handler for click on pay button. Not the form-submit event.
    // TODO Document why. Does Apple Pay button submit the <form>?
    // @see https://tixtrackteam.slack.com/archives/C02CMPTFLKF/p1746147319477799?thread_ts=1746146976.910289&cid=C02CMPTFLKF
    onSubmit() {
      onSubmit(this.context, this.$router, () => this.$emit('submit'))
    }

    get darkTheme(): boolean {
      return theme?.cartThemeMode === 'dark' || theme?.themeMode === 'dark'
    }

    get isModifiable(): boolean {
      return store.getters['Cart/isModifiable']
    }

    get hasScheduledPayments(): boolean {
      return this.$store.getters['Cart/hasScheduledPayments']
    }

    get outstandingBalance(): number {
      return this.$store.getters['Cart/outstandingBalance']
    }

    get blocked(): boolean {
      return store.getters['Cart/blockingRules']?.length > 0
    }

    get hidePayButton() {
      return this.context === 'checkout' && this.blocked
    }

    handleRemoveItem(item: CartItem, index: number) {
      this.pendingRemovalCartItemId = item.id
      this.pendingRemovalTriggerElement = this.$refs[`removeButton_${index}`][0] as HTMLElement
    }

    handleRemoveItemClose() {
      this.pendingRemovalCartItemId = null
      this.pendingRemovalTriggerElement = null
    }

    toggleBundle(bundleId: string) {
      this.$set(this.expandedBundles, bundleId, !this.expandedBundles[bundleId])
    }
  }
